import {
  Box,
  Flex,
  Heading,
  HStack,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  Spinner,
  Stack,
  Text,
  Textarea,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import {IModalProps, Modal} from 'components/shared';
import {useRouter} from 'next/router';
import React, {FC, useEffect, useMemo, useState} from 'react';
import {colors, hiddenScrollBarStyles, modalMetroCloseButtonStyles, pinkScrollBarStyles} from 'theme';
import {IProduct, IRestaurantResponse} from 'types';

import {useDesktop, useTranslation} from '../../../../hooks';
import {AlertClearCart} from '../../../pages/restaurant';
import {useCartContext} from '../../../providers';
import {useMetroProductDetails} from '../../api/queries';
import {useMetroCart} from '../../hooks/useMetroCart';
import MetroBreadcrumb, {IBreadcrumb} from '../Breadcrumb';
import {ProductEditButton} from '../metro-product-edit-modal';
import {ModalActionBar} from '../ModalActionBar';
import {ImageSlider} from './components/image-slider/ImageSlider';

interface Props extends IModalProps {
  productSlug: string;
  provider?: IRestaurantResponse;
}

export const MetroProductDetailsModal: FC<Props> = ({isOpen, onClose, provider, productSlug}) => {
  const {
    data: productDetails,
    isLoading: isLoadingProductDetails,
    error,
    refetch,
  } = useMetroProductDetails(productSlug);
  const {isOpen: isOpenAlertClearCart, onOpen: onOpenAlertClearCart, onClose: onCloseAlertClearCart} = useDisclosure();
  const {t} = useTranslation();
  const {cartItems, editCartItem} = useCartContext();
  const isDesktop = useDesktop();
  const {locale} = useRouter();
  const [comment, setComment] = useState('');

  const product = productDetails?.product;

  const {
    name,
    details,
    storage_instructions,
    brand,
    country_of_origin,
    ingredients_csv,
    mass_measurement,
    quantity_value,
    product_img_for_detail_thumbnail,
    product_img_2_thumbnail,
    product_img_3_thumbnail,
    price,
    is_weighted_product,
    stock_quantity,
    old_price,
  } = product || {};

  const images = useMemo(
    () =>
      [product_img_for_detail_thumbnail, product_img_2_thumbnail, product_img_3_thumbnail].filter(
        (src): src is string => !!src,
      ),
    [product_img_for_detail_thumbnail, product_img_2_thumbnail, product_img_3_thumbnail],
  );

  const cartProduct = useMemo(
    () => (product ? cartItems.find((i) => i.productId === product.id) : undefined),
    [cartItems, product],
  );
  const currentQuantity = cartProduct?.quantity ?? 0;
  const currentCartItemId = cartProduct?.id;

  const {handleAddProduct, handleDelete, cartProvider, localQuantity, handleClearAndAdd} = useMetroCart({
    product: product || ({} as IProduct),
    provider,
    quantity: currentQuantity,
    cartItemId: currentCartItemId,
    onOpenAlertClearCart: onOpenAlertClearCart,
  });

  const isNoStock = stock_quantity === 0;

  useEffect(() => {
    if (cartProduct?.instructions) setComment(cartProduct.instructions);
  }, [cartProduct?.instructions]);

  const handleCommentChange = (value: string) => {
    setComment(value);
  };

  const handleCommentBlur = () => {
    if (cartProduct) {
      const updatedCartItem = {
        ...cartProduct,
        instructions: comment,
      };
      editCartItem(updatedCartItem);
    }
  };

  useEffect(() => {
    if (isOpen && productSlug) {
      const newUrl = `/${locale}/market-product/${productSlug}`;
      window.history.replaceState(null, '', newUrl);
    }
  }, [isOpen, locale, productSlug]);

  const breadcrumbs = useMemo(() => {
    if (!productDetails?.categories_tree || productDetails.categories_tree.length === 0) {
      return [];
    }

    const createBreadcrumbs = (categories: any[]): IBreadcrumb[] => {
      const result: IBreadcrumb[] = [];

      result.push({
        label: t('MAIN'),
        href: '/metro',
      });

      const processCategory = (category: any) => {
        result.push({
          label: category.name,
          href: `/categories/${category.slug}`,
        });

        if (category.children && category.children.length > 0) {
          processCategory(category.children[0]);
        }
      };

      processCategory(categories[0]);

      if (result.length > 0) {
        result[result.length - 1] = {
          ...result[result.length - 1],
          isCurrentPage: true,
        };
      }

      return result;
    };

    return createBreadcrumbs(productDetails.categories_tree);
  }, [productDetails?.categories_tree, t]);

  if (isLoadingProductDetails) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} scrollBehavior>
        <ModalContent
          h={['100vh', '82vh']}
          overflow="hidden"
          borderRadius="3xl"
          maxW={['100%', '900px']}
          display="flex"
          flexDirection="column"
        >
          <ModalCloseButton sx={modalMetroCloseButtonStyles} zIndex={10} />
          <ModalBody p={2} flex="1" minH={0} display="flex" alignItems="center" justifyContent="center">
            <Spinner size="xl" color="pink.500" />
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  if (error || !product) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} scrollBehavior>
        <ModalContent
          h={['90vh', '82vh']}
          overflow="hidden"
          borderRadius="3xl"
          maxW={['90%', '900px']}
          display="flex"
          flexDirection="column"
        >
          <ModalCloseButton sx={modalMetroCloseButtonStyles} zIndex={10} />
          <ModalBody p={2} flex="1" minH={0} display="flex" alignItems="center" justifyContent="center">
            <Text fontSize="lg" color="red.500">
              {t('SOMETHING_WENT_WRONG')}
            </Text>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  /*const recommendations = (
    <Box
      mt={6}
      mb={[8, 0]}
      px={[0, 4]}
      minH={0}
      borderTop={!isDesktop ? '1px' : 0}
      paddingTop={!isDesktop ? '10px' : 0}
      borderColor={colors.lightGray}
    >
      <Text fontWeight="bold" mb={3}>
        {t('WILL_BE_USEFUL')}
      </Text>
      <HStack
        overflowX="auto"
        spacing="2"
        align="flex-start"
        flexWrap="nowrap"
        minH={0}
        sx={{
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((_, idx) => (
          <Box key={idx} minW="120px" display="flex" flexDirection="column" textAlign="center" alignItems="center">
            <Image
              src={product_img_for_detail_thumbnail}
              alt="item"
              borderRadius="md"
              boxSize="100px"
              objectFit="contain"
              fallbackSrc="/food_pattern.webp"
            />
            <Text fontSize="xs" mt={1}>
              Пример товара
            </Text>
            <Text fontWeight="semibold" fontSize="sm">
              {`275 ${t('CURRENCY')}`}
            </Text>
          </Box>
        ))}
      </HStack>
    </Box>
  );*/

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} scrollBehavior fullSize={!isDesktop}>
        <ModalContent
          overflow="hidden"
          borderRadius="3xl"
          marginTop={isDesktop ? undefined : '5%'}
          maxW={['100%', '900px']}
          display="flex"
          flexDirection="column"
          h={['auto', '82vh']}
          paddingBottom={isDesktop ? undefined : 'calc(env(safe-area-inset-bottom) + 5%)'}
        >
          <ModalCloseButton
            sx={
              isDesktop
                ? modalMetroCloseButtonStyles
                : {
                    bg: colors.lightGray,
                    WebkitTapHighlightColor: 'transparent',
                  }
            }
            zIndex={10}
          />
          <Box position="absolute" top={12} right={3} zIndex={20}>
            <ProductEditButton product={product} onSuccess={refetch} onCloseParent={onClose} />
          </Box>
          <ModalBody p={2} flex="1" minH={0} overflowY="auto" sx={pinkScrollBarStyles}>
            <Flex direction={['column', 'row']} height={['auto', '100%']}>
              {/* Left Side */}
              <Box
                flex={['none', '1']}
                p={4}
                pt={2}
                sx={!isDesktop ? {} : hiddenScrollBarStyles}
                overflowY={isDesktop ? 'auto' : 'visible'}
              >
                {/* Breadcrumbs */}
                {breadcrumbs.length > 0 && (
                  <Box mb={4} px={2}>
                    <MetroBreadcrumb items={breadcrumbs} onItemClick={onClose} isInModal={true} />
                  </Box>
                )}

                <ImageSlider
                  images={images}
                  productId={product.id}
                  oldPrice={old_price}
                  price={price}
                  product={product}
                  onSuccess={refetch}
                  onCloseParent={onClose}
                />

                {/*{isDesktop && recommendations}*/}
              </Box>

              {/* Right Side */}
              <Box
                flex={['none', '1']}
                pr={1}
                paddingInline={['2', '0']}
                display="flex"
                flexDirection="column"
                minH={0}
              >
                <Box
                  flex={['none', '1']}
                  pt={2}
                  sx={!isDesktop ? {} : hiddenScrollBarStyles}
                  overflowY={isDesktop ? 'auto' : 'visible'}
                  minH={0}
                >
                  <VStack align="flex-start" spacing={4} pb={[0, 5]}>
                    <VStack align="flex-start" mb={8}>
                      <HStack justify="space-between" w="full">
                        <Heading fontSize="xl" maxW="350px">
                          {name}
                        </Heading>
                      </HStack>
                      {is_weighted_product && quantity_value !== 0 && quantity_value !== null && (
                        <Text fontSize="xl" color={colors.inputBgGray1} alignSelf="flex-start" mt="0px">
                          {`${quantity_value} ${mass_measurement}`}
                        </Text>
                      )}
                    </VStack>

                    {details && (
                      <Box w="full" minH={0} paddingTop={4} borderTop="1px" borderColor={colors.lightGray}>
                        <Text fontWeight="medium" fontSize="sm">
                          {details}
                        </Text>
                      </Box>
                    )}
                    <Stack spacing={4}>
                      {ingredients_csv && (
                        <Box w="full" minH={0} paddingY={4} borderY="1px" borderColor={colors.lightGray}>
                          <Text fontWeight="semibold" color={colors.inputBgGray1} fontSize="sm">
                            {t('INGREDIENTS')}
                          </Text>
                          <Text fontWeight="medium" fontSize="sm">
                            {ingredients_csv}
                          </Text>
                        </Box>
                      )}
                      {storage_instructions && (
                        <Box>
                          <Text fontWeight="semibold" color={colors.inputBgGray1} fontSize="sm">
                            {t('STORAGE_INSTRUCTIONS')}
                          </Text>
                          <Text fontWeight="medium" fontSize="sm">
                            {storage_instructions}
                          </Text>
                        </Box>
                      )}
                      {brand && (
                        <Box>
                          <Text fontWeight="semibold" color={colors.inputBgGray1} fontSize="sm">
                            {t('BRAND')}
                          </Text>
                          <Text fontWeight="medium" fontSize="sm">
                            {brand}
                          </Text>
                        </Box>
                      )}
                      {country_of_origin && (
                        <Box>
                          <Text fontWeight="semibold" color={colors.inputBgGray1} fontSize="sm">
                            {t('COUNTRY_OF_ORIGIN')}
                          </Text>
                          <Text fontWeight="medium" fontSize="sm">
                            {country_of_origin}
                          </Text>
                        </Box>
                      )}
                    </Stack>
                  </VStack>
                  {/*{!isDesktop && recommendations}*/}
                </Box>
                <Box paddingRight={[0, 2]} paddingBottom={[2, 2]} mt={[4, 0]}>
                  {/* Comment input - only show if product is in cart */}
                  {cartProduct?.quantity && (
                    <Box
                      mb={4}
                      mx={2}
                      bg="gray.50"
                      p={4}
                      borderRadius="12px"
                      border="1px solid"
                      borderColor="gray.200"
                      boxShadow="0 1px 3px rgba(0, 0, 0, 0.1)"
                    >
                      <Text fontWeight="semibold" fontSize="sm" mb={3} color={colors.black}>
                        {t('commentForPicker')}
                      </Text>
                      <Textarea
                        id="comment"
                        name="comment"
                        value={comment}
                        onChange={(e) => handleCommentChange(e.target.value)}
                        onBlur={handleCommentBlur}
                        placeholder={t('SPECIAL_MENTIONS')}
                        maxLength={300}
                        fontSize="14px"
                        rows={3}
                      />
                    </Box>
                  )}

                  {isDesktop && (
                    <ModalActionBar
                      cartProduct={cartProduct}
                      localQuantity={localQuantity}
                      isNoStock={isNoStock}
                      price={price}
                      oldPrice={old_price}
                      onAdd={handleAddProduct}
                      onDelete={handleDelete}
                      currentCartItemId={currentCartItemId}
                    />
                  )}
                </Box>
              </Box>
            </Flex>
          </ModalBody>
          {!isDesktop && (
            <Box px={4}>
              <ModalActionBar
                cartProduct={cartProduct}
                localQuantity={localQuantity}
                isNoStock={isNoStock}
                price={price}
                oldPrice={old_price}
                onAdd={handleAddProduct}
                onDelete={handleDelete}
                currentCartItemId={currentCartItemId}
              />
            </Box>
          )}
        </ModalContent>
      </Modal>
      {isOpenAlertClearCart && (
        <AlertClearCart
          isOpen={isOpenAlertClearCart}
          onClose={onCloseAlertClearCart}
          onConfirm={handleClearAndAdd}
          providerName={cartProvider?.name}
        />
      )}
    </>
  );
};
